/**
 * Pure helpers for the merged GPS Tracker "Trips" report (formerly two separate reports, Trip
 * Route and Trip Summary, which listed the very same trips from the same backend trip logic).
 *
 * The Trips list is loaded from the fleet-wide `trip-summary` endpoint (supports many trackers at
 * once, carries idle time + alert breakdown); a trip's polyline is loaded lazily from the per-tracker
 * `trip-route` endpoint only when the user opens it on the map. Both endpoints split trips with
 * the same backend `consolidateIntoTrips` pass and stamp the same `sessionId`
 * (`{deviceId}-{epochSecondsOfStart}`), so a summary row is matched to its route by that id.
 */

const ms = (t) => (t == null ? NaN : new Date(t).getTime());

/**
 * Find the route session for a summary row. Prefers an exact `sessionId` match; falls back to the
 * route whose time window overlaps the trip the most (defensive — e.g. a client-side consolidation
 * pass merged slices differently against an older API). Returns null when nothing overlaps.
 */
export function findRouteForTrip(routes, trip) {
  if (!Array.isArray(routes) || !trip) return null;
  const exact = routes.find((r) => r.sessionId && r.sessionId === trip.sessionId);
  if (exact) return exact;

  const tStart = ms(trip.startTime);
  const tEnd = ms(trip.endTime);
  if (!Number.isFinite(tStart) || !Number.isFinite(tEnd)) return null;

  let best = null;
  let bestOverlap = 0;
  for (const r of routes) {
    const overlap = Math.min(tEnd, ms(r.endTime)) - Math.max(tStart, ms(r.startTime));
    if (Number.isFinite(overlap) && overlap > bestOverlap) {
      bestOverlap = overlap;
      best = r;
    }
  }
  return best;
}

/** Sum of an `{ALERT_TYPE: count}` map. */
export function totalAlerts(alerts) {
  if (!alerts || typeof alerts !== "object") return 0;
  return Object.values(alerts).reduce((sum, v) => sum + (Number(v) || 0), 0);
}

/** `{POWER_CUT: 7, LOW_BATTERY: 1}` → "Power cut 7 · Low battery 1" (empty string when none). */
export function formatAlertBreakdown(alerts) {
  if (!alerts || typeof alerts !== "object") return "";
  return Object.entries(alerts)
    .filter(([, v]) => Number(v) > 0)
    .map(([k, v]) => {
      const words = k.toLowerCase().replace(/_/g, " ");
      return `${words.charAt(0).toUpperCase()}${words.slice(1)} ${v}`;
    })
    .join(" · ");
}

/**
 * Per-tracker trip ordinal ("Trip 1", "Trip 2" … restarting for each tracker), so a multi-tracker
 * result doesn't read "Trip 7" for a vehicle's first trip of the day. Returns an array parallel to
 * `trips`.
 */
export function perAssetTripNumbers(trips) {
  const counters = new Map();
  return (trips || []).map((t) => {
    const k = t.assetUnitId || "?";
    const n = (counters.get(k) || 0) + 1;
    counters.set(k, n);
    return n;
  });
}
